/**
 * Page Visibility Handler for LiveKit
 *
 * Distinguishes temporary visibility/dialog noise from actual document unload.
 *
 * IMPORTANT: Do not treat `beforeunload` as a confirmed leave — it fires while the browser’s
 * “leave site?” dialog is showing. Teardown belongs on non-persisted `pagehide` (soft/delayed path)
 * and `unload` (confirmed navigation away).
 */

import { getPageHideReason, type LiveKitPageLeaveReason } from './pageLifecyclePolicy'

interface DialogEvent {
  type: string
  timestamp: number
}

interface PageVisibilityState {
  isPageVisible: boolean
  lastVisibilityChange: number
  dialogEvents: DialogEvent[]
  isDialogOpen: boolean
  shouldDisconnect: boolean
}

class PageVisibilityHandler {
  private state: PageVisibilityState = {
    isPageVisible: true,
    lastVisibilityChange: 0,
    dialogEvents: [],
    isDialogOpen: false,
    shouldDisconnect: false
  }

  private disconnectTimeout: NodeJS.Timeout | null = null
  private readonly boundHandleVisibilityChange = this.handleVisibilityChange.bind(this)
  private readonly boundHandleFreeze = this.handleFreeze.bind(this)
  private readonly boundHandleResume = this.handleResume.bind(this)
  private readonly boundHandleDialogOpened = this.handleDialogOpened.bind(this)
  private readonly boundHandleDialogClosed = this.handleDialogClosed.bind(this)
  private readonly boundHandleModalOpened = this.handleModalOpened.bind(this)
  private readonly boundHandleModalClosed = this.handleModalClosed.bind(this)
  private readonly boundHandleAlertOpened = this.handleAlertOpened.bind(this)
  private readonly boundHandleAlertClosed = this.handleAlertClosed.bind(this)
  private readonly boundHandleBeforeUnload = this.handleBeforeUnload.bind(this)
  private readonly boundHandlePageHide = this.handlePageHide.bind(this)
  private readonly boundHandleUnload = this.handleUnload.bind(this)

  constructor() {
    this.setupEventListeners()
  }

  private setupEventListeners() {
    // Listen for page visibility changes (for state tracking only - no disconnect on tab switch)
    document.addEventListener('visibilitychange', this.boundHandleVisibilityChange)
    
    // Page Lifecycle API (Chrome 68+) - cooperate with browser when tab is frozen
    document.addEventListener('freeze', this.boundHandleFreeze)
    document.addEventListener('resume', this.boundHandleResume)
    
    // Listen for dialog events
    window.addEventListener('dialog-opened', this.boundHandleDialogOpened)
    window.addEventListener('dialog-closed', this.boundHandleDialogClosed)
    window.addEventListener('modal-opened', this.boundHandleModalOpened)
    window.addEventListener('modal-closed', this.boundHandleModalClosed)
    window.addEventListener('alert-opened', this.boundHandleAlertOpened)
    window.addEventListener('alert-closed', this.boundHandleAlertClosed)
    
    // beforeunload: listener kept as no-op so we never disconnect during the confirmation dialog.
    // Teardown runs from pagehide (delayed path) + unload below.
    window.addEventListener('beforeunload', this.boundHandleBeforeUnload)
    window.addEventListener('pagehide', this.boundHandlePageHide)
    window.addEventListener('unload', this.boundHandleUnload)
  }

  private handleVisibilityChange() {
    const isVisible = !document.hidden
    const now = Date.now()
    
    console.log(`📱 [Page Visibility] Page ${isVisible ? 'visible' : 'hidden'}`)
    
    this.state.isPageVisible = isVisible
    this.state.lastVisibilityChange = now

    if (isVisible) {
      // Page became visible - cancel any pending disconnection and reset state
      this.cancelDisconnection()
      this.state.shouldDisconnect = false
      console.log('📱 [Page Visibility] Page visible - resetting disconnection state')
      window.dispatchEvent(new CustomEvent('page-visible', { detail: { timestamp: now } }))
    } else {
      window.dispatchEvent(new CustomEvent('page-hidden', { detail: { timestamp: now } }))
    }
    // OPTION A (Call Stability Fix): Do NOT schedule disconnect on visibilitychange alone.
    // Tab switches, window minimize, and system dialogs cause visibilitychange but the user
    // has not actually left. Real leave signals come from unload and non-BFCache pagehide.
  }

  private handleFreeze() {
    console.log('📱 [Page Visibility] Page frozen (browser suspending)')
    window.dispatchEvent(new CustomEvent('page-frozen', { detail: { timestamp: Date.now() } }))
  }

  private handleResume() {
    console.log('📱 [Page Visibility] Page resumed (browser unfreezing)')
    window.dispatchEvent(new CustomEvent('page-resumed', { detail: { timestamp: Date.now() } }))
  }

  private handleDialogOpened(event: CustomEvent) {
    console.log(`📱 [Dialog Detection] Dialog opened: ${event.detail?.type || 'unknown'}`)
    this.state.isDialogOpen = true
    this.state.dialogEvents.push({
      type: 'dialog-opened',
      timestamp: Date.now()
    })
    this.cancelDisconnection()
  }

  private handleDialogClosed(event: CustomEvent) {
    this.state.dialogEvents.push({
      type: 'dialog-closed',
      timestamp: Date.now()
    })
    this.state.isDialogOpen = false
  }

  private handleModalOpened(event: CustomEvent) {
    console.log(`📱 [Dialog Detection] Modal opened: ${event.detail?.type || 'unknown'}`)
    this.state.isDialogOpen = true
    this.cancelDisconnection()
  }

  private handleModalClosed(event: CustomEvent) {
    console.log(`📱 [Dialog Detection] Modal closed: ${event.detail?.type || 'unknown'}`)
    this.state.isDialogOpen = false
  }

  private handleAlertOpened(event: CustomEvent) {
    console.log(`📱 [Dialog Detection] Alert opened: ${event.detail?.type || 'unknown'}`)
    this.state.isDialogOpen = true
    this.cancelDisconnection()
  }

  private handleAlertClosed(event: CustomEvent) {
    console.log(`📱 [Dialog Detection] Alert closed: ${event.detail?.type || 'unknown'}`)
    this.state.isDialogOpen = false
  }

  private handleBeforeUnload() {
    // Intentionally empty: the native unload prompt fires beforeunload while the tab may stay.
    // Running disconnect/cleanup here leaves the session dead if the user clicks Cancel.
    console.log('📱 [Page Visibility] beforeunload observed — awaiting pagehide/unload for teardown')
  }

  private handlePageHide(event: Event) {
    const reason = getPageHideReason(event as PageTransitionEvent)

    if (!reason) {
      console.log('📱 [Page Visibility] Page hide detected with BFCache persistence - keeping LiveKit alive')
      this.state.shouldDisconnect = false
      return
    }

    console.log('📱 [Page Visibility] Page hide detected - scheduling graceful disconnect')
    this.state.shouldDisconnect = true
    this.dispatchLiveKitPageLeave(reason)
  }

  private handleUnload() {
    console.log('📱 [Page Visibility] Unload detected - marking for disconnection')
    this.state.shouldDisconnect = true
    this.dispatchLiveKitPageLeave()
    this.triggerImmediateCleanup()
  }

  private dispatchLiveKitPageLeave(reason: LiveKitPageLeaveReason = 'page-leave') {
    window.dispatchEvent(new CustomEvent('livekit-page-leave-detected', {
      detail: { reason, timestamp: Date.now() }
    }))
  }

  // Trigger immediate cleanup when page is being unloaded
  private triggerImmediateCleanup() {
    try {
      // Import and trigger network cleanup
      import('../services/Network').then(({ default: Network }) => {
        // Get the global network instance and clean it up
        if (window.networkInstance) {
          console.log('📱 [Page Visibility] Triggering immediate network cleanup')
          window.networkInstance.cleanup()
        }
      }).catch(error => {
        console.warn('📱 [Page Visibility] Failed to import Network for cleanup:', error)
      })
    } catch (error) {
      console.warn('📱 [Page Visibility] Failed to trigger immediate cleanup:', error)
    }
  }

  private cancelDisconnection() {
    if (this.disconnectTimeout) {
      console.log('📱 [Page Visibility] Canceling scheduled disconnection')
      clearTimeout(this.disconnectTimeout)
      this.disconnectTimeout = null
    }
  }

  public shouldDisconnect(): boolean {
    return this.state.shouldDisconnect
  }

  /** Whether the page is currently visible (tab focused). Use for throttling when hidden. */
  public isPageVisible(): boolean {
    return this.state.isPageVisible
  }

  /**
   * Whether any tracked dialog/modal/alert is currently open. This is a live flag (set on
   * open, cleared on close) — unlike the connector's fixed-window dialog grace — so a
   * long-lived modal such as the whiteboard keeps suppressing page-leave teardown for its
   * whole lifetime (SC-246).
   */
  public isDialogOpen(): boolean {
    return this.state.isDialogOpen
  }

  public reset() {
    this.state.shouldDisconnect = false
    this.cancelDisconnection()
  }

  public destroy() {
    this.cancelDisconnection()
    
    // Remove event listeners
    document.removeEventListener('visibilitychange', this.boundHandleVisibilityChange)
    document.removeEventListener('freeze', this.boundHandleFreeze)
    document.removeEventListener('resume', this.boundHandleResume)
    window.removeEventListener('dialog-opened', this.boundHandleDialogOpened)
    window.removeEventListener('dialog-closed', this.boundHandleDialogClosed)
    window.removeEventListener('modal-opened', this.boundHandleModalOpened)
    window.removeEventListener('modal-closed', this.boundHandleModalClosed)
    window.removeEventListener('alert-opened', this.boundHandleAlertOpened)
    window.removeEventListener('alert-closed', this.boundHandleAlertClosed)
    window.removeEventListener('beforeunload', this.boundHandleBeforeUnload)
    window.removeEventListener('pagehide', this.boundHandlePageHide)
    window.removeEventListener('unload', this.boundHandleUnload)
  }
}

// Export singleton instance
export const pageVisibilityHandler = new PageVisibilityHandler()

// Export utility functions for components
export const emitDialogOpened = (dialogType: string = 'unknown') => {
  console.log(`📱 [Dialog Detection] ${dialogType} dialog opened`)
  window.dispatchEvent(new CustomEvent('dialog-opened', { 
    detail: { type: dialogType, timestamp: Date.now() } 
  }))
}

export const emitDialogClosed = (dialogType: string = 'unknown') => {
  window.dispatchEvent(new CustomEvent('dialog-closed', { 
    detail: { type: dialogType, timestamp: Date.now() } 
  }))
}

export const emitModalOpened = (modalType: string = 'unknown') => {
  console.log(`📱 [Dialog Detection] ${modalType} modal opened`)
  window.dispatchEvent(new CustomEvent('modal-opened', { 
    detail: { type: modalType, timestamp: Date.now() } 
  }))
}

export const emitModalClosed = (modalType: string = 'unknown') => {
  console.log(`📱 [Dialog Detection] ${modalType} modal closed`)
  window.dispatchEvent(new CustomEvent('modal-closed', { 
    detail: { type: modalType, timestamp: Date.now() } 
  }))
}

export const emitAlertOpened = (alertType: string = 'unknown') => {
  console.log(`📱 [Dialog Detection] ${alertType} alert opened`)
  window.dispatchEvent(new CustomEvent('alert-opened', { 
    detail: { type: alertType, timestamp: Date.now() } 
  }))
}

export const emitAlertClosed = (alertType: string = 'unknown') => {
  console.log(`📱 [Dialog Detection] ${alertType} alert closed`)
  window.dispatchEvent(new CustomEvent('alert-closed', { 
    detail: { type: alertType, timestamp: Date.now() } 
  }))
}
